import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, onSnapshot, doc, addDoc, updateDoc,
  deleteDoc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useShopPack } from '../../hooks/useShopPack';
import Loading from '../../components/common/Loading';
import {
  ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff,
  Tag, Package, ImagePlus, X, Check, Crown,
  AlertTriangle, Loader,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   Constantes
───────────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  published: { label: 'Publié',   color: '#00c9a7', bg: 'rgba(0,201,167,0.10)' },
  draft:     { label: 'Brouillon', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
};

/* ─────────────────────────────────────────────────────────────────
   Upload image via Cloudinary (unsigned upload)
   Variables dans .env.local :
     REACT_APP_CLOUDINARY_CLOUD_NAME = ton-cloud-name
     REACT_APP_CLOUDINARY_UPLOAD_PRESET = ton-preset-unsigned
───────────────────────────────────────────────────────────────── */
const CLOUD_NAME    = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const uploadImage = (shopId, file, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);
    formData.append('folder', `yeldi/shops/${shopId}/products`);
    formData.append('public_id', `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        resolve(data.secure_url);
      } else {
        reject(new Error(`Cloudinary error: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(formData);
  });

/* Cloudinary deletion requires server-side signature — on retire
   juste l'URL de la liste des images (la suppression réelle peut
   se faire depuis le dashboard Cloudinary ou via un Worker) */
const deleteImage = async (_url) => {
  /* No-op côté client — Cloudinary nécessite une signature serveur
     pour supprimer. Les images orphelines peuvent être nettoyées
     depuis le dashboard Cloudinary ou via un Cloudflare Worker. */
};

/* ─────────────────────────────────────────────────────────────────
   Modal CRUD produit / catégorie
───────────────────────────────────────────────────────────────── */
const CrudModal = ({ mode, item, categories, shopId, pack, onClose, onSaved }) => {
  const isProduct  = mode === 'product' || mode === 'edit-product';
  const isEdit     = mode.startsWith('edit');

  const [name,        setName       ] = useState(item?.name        || '');
  const [description, setDescription] = useState(item?.description || '');
  const [price,       setPrice      ] = useState(item?.price       !== undefined ? String(item.price) : '');
  const [showPrice,   setShowPrice  ] = useState(item?.showPrice   !== false);
  const [categoryId,  setCategoryId ] = useState(item?.categoryId  || '');
  const [status,      setStatus     ] = useState(item?.status      || 'published');
  const [images,      setImages     ] = useState(item?.images      || []);
  const [uploading,   setUploading  ] = useState(false);
  const [uploadPct,   setUploadPct  ] = useState(0);
  const [saving,      setSaving     ] = useState(false);
  const [error,       setError      ] = useState('');
  const fileRef = useRef();

  const maxImages = pack.maxImages === Infinity ? 99 : pack.maxImages;

  const handleImageAdd = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (images.length + files.length > maxImages) {
      setError(`Votre pack autorise ${maxImages} image(s) par produit maximum.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const urls = [];
      for (const file of files) {
        const url = await uploadImage(shopId, file, setUploadPct);
        urls.push(url);
      }
      setImages(prev => [...prev, ...urls]);
    } catch {
      setError("Erreur lors de l'upload de l'image.");
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleImageRemove = async (url, idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    await deleteImage(url);
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Le nom est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      const colRef = isProduct
        ? collection(db, 'shops', shopId, 'products')
        : collection(db, 'shops', shopId, 'categories');

      const data = isProduct
        ? {
            name:        name.trim(),
            description: description.trim(),
            price:       price !== '' ? parseFloat(price) : null,
            showPrice,
            categoryId:  categoryId || null,
            status,
            images,
            updatedAt:   serverTimestamp(),
          }
        : {
            name:      name.trim(),
            status,
            updatedAt: serverTimestamp(),
          };

      if (isEdit && item?.id) {
        await updateDoc(doc(colRef, item.id), data);
      } else {
        await addDoc(colRef, { ...data, createdAt: serverTimestamp() });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError('Erreur lors de la sauvegarde : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        animation: 'cmFadeIn 0.16s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          borderRadius: 'var(--r-xl)',
          width: '100%', maxWidth: 400,
          maxHeight: '88dvh', overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
          animation: 'cmPopIn 0.20s cubic-bezier(0.34,1.4,0.64,1)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.65rem',
          padding: '1rem 1rem 0.75rem',
          borderBottom: '1px solid var(--outline)',
          position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 'var(--r-sm)',
            background: isProduct ? 'rgba(0,201,167,0.10)' : 'rgba(99,102,241,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {isProduct ? <Package size={17} color="#00c9a7" strokeWidth={2}/> : <Tag size={17} color="#6366f1" strokeWidth={2}/>}
          </div>
          <div style={{ flex: 1, fontWeight: 800, fontSize: '0.9rem', color: 'var(--text)' }}>
            {isEdit
              ? `Modifier ${isProduct ? 'le produit' : 'la catégorie'}`
              : `Nouveau ${isProduct ? 'produit' : 'catégorie'}`}
          </div>
          <button onClick={onClose} style={{
            background: 'var(--bg-elevated)', border: 'none',
            borderRadius: '50%', width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0,
          }}>
            <X size={13}/>
          </button>
        </div>

        {/* Corps */}
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>

          {/* Nom */}
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.35rem' }}>
              Nom *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={isProduct ? 'Ex: Croissant au beurre' : 'Ex: Viennoiseries'}
              className="form-input"
              autoFocus
            />
          </div>

          {/* Description (produit uniquement) */}
          {isProduct && (
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.35rem' }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Courte description du produit…"
                rows={2}
                className="form-input"
                style={{ resize: 'none', lineHeight: 1.5 }}
              />
            </div>
          )}

          {/* Prix (produit uniquement) */}
          {isProduct && (
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.35rem' }}>
                Prix (DA)
              </label>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                  type="number" min="0"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  placeholder="Ex: 250"
                  className="form-input"
                  style={{ flex: 1 }}
                />
                <button
                  onClick={() => setShowPrice(v => !v)}
                  title={showPrice ? 'Masquer le prix' : 'Afficher le prix'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0.55rem 0.75rem',
                    borderRadius: 'var(--r-md)',
                    border: '1.5px solid var(--outline)',
                    background: showPrice ? 'rgba(0,201,167,0.08)' : 'var(--bg-elevated)',
                    color: showPrice ? '#00c9a7' : 'var(--text-3)',
                    fontSize: '0.72rem', fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {showPrice ? <Eye size={13}/> : <EyeOff size={13}/>}
                  {showPrice ? 'Visible' : 'Masqué'}
                </button>
              </div>
            </div>
          )}

          {/* Catégorie (produit uniquement) */}
          {isProduct && categories.length > 0 && (
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.35rem' }}>
                Catégorie
              </label>
              <select
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                className="form-input"
              >
                <option value="">— Sans catégorie —</option>
                {categories.filter(c => c.status === 'published').map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Images (produit uniquement) */}
          {isProduct && (
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.35rem' }}>
                Images ({images.length}/{maxImages})
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {images.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
                    <img src={url} alt="" style={{ width: 64, height: 64, borderRadius: 'var(--r-sm)', objectFit: 'cover', boxShadow: 'var(--neu-sm)' }}/>
                    <button
                      onClick={() => handleImageRemove(url, i)}
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        width: 20, height: 20, borderRadius: '50%',
                        background: '#ef4444', border: '2px solid var(--bg)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', padding: 0,
                      }}
                    >
                      <X size={9} color="#fff" strokeWidth={3}/>
                    </button>
                  </div>
                ))}
                {images.length < maxImages && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    style={{
                      width: 64, height: 64,
                      borderRadius: 'var(--r-sm)',
                      border: '2px dashed var(--outline)',
                      background: 'var(--bg-elevated)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      color: 'var(--text-3)', gap: '0.2rem',
                    }}
                  >
                    {uploading ? (
                      <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }}/><span style={{ fontSize: '0.55rem' }}>{uploadPct}%</span></>
                    ) : (
                      <><ImagePlus size={14}/><span style={{ fontSize: '0.55rem', fontWeight: 600 }}>Ajouter</span></>
                    )}
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageAdd}/>
              </div>
            </div>
          )}

          {/* Statut */}
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.35rem' }}>
              Statut
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  style={{
                    flex: 1, padding: '0.55rem',
                    borderRadius: 'var(--r-md)',
                    border: `2px solid ${status === key ? cfg.color : 'var(--outline)'}`,
                    background: status === key ? cfg.bg : 'var(--bg-elevated)',
                    color: status === key ? cfg.color : 'var(--text-2)',
                    fontWeight: 700, fontSize: '0.8rem',
                    cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all 0.12s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                  }}
                >
                  {key === 'published' ? <Eye size={13}/> : <EyeOff size={13}/>}
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Erreur */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.65rem 0.75rem',
              borderRadius: 'var(--r-md)',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.20)',
              fontSize: '0.78rem', color: '#ef4444', fontWeight: 500,
            }}>
              <AlertTriangle size={13} style={{ flexShrink: 0 }}/>
              {error}
            </div>
          )}

          {/* Boutons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            <button onClick={onClose} style={{
              flex: 1, padding: '0.72rem',
              borderRadius: 'var(--r-lg)',
              border: '1.5px solid var(--outline)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-2)', fontWeight: 700, fontSize: '0.85rem',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 2, padding: '0.72rem',
                borderRadius: 'var(--r-lg)', border: 'none',
                background: saving ? 'var(--outline)' : 'linear-gradient(135deg, #1b2a6b, #00c9a7)',
                color: '#fff', fontWeight: 700, fontSize: '0.85rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                boxShadow: saving ? 'none' : '0 3px 12px rgba(0,201,167,0.30)',
              }}
            >
              {saving ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }}/> Sauvegarde…</> : <><Check size={14}/> Enregistrer</>}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cmFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes cmPopIn  { from{opacity:0;transform:scale(.93)} to{opacity:1;transform:scale(1)} }
        @keyframes spin     { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   Confirmation de suppression
───────────────────────────────────────────────────────────────── */
const DeleteConfirm = ({ label, onConfirm, onClose }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem' }}>
    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg)', borderRadius: 'var(--r-xl)', padding: '1.25rem', width: '100%', maxWidth: 320, boxShadow: '0 8px 40px rgba(0,0,0,0.28)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--r-lg)', background: 'rgba(239,68,68,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.85rem' }}>
        <Trash2 size={20} color="#ef4444" strokeWidth={2}/>
      </div>
      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text)', textAlign: 'center', marginBottom: '0.4rem' }}>Supprimer ?</div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-2)', textAlign: 'center', marginBottom: '1rem', lineHeight: 1.5 }}>
        "{label}" sera définitivement supprimé.
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={onClose} style={{ flex: 1, padding: '0.65rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text-2)', fontWeight: 700, fontSize: '0.83rem', cursor: 'pointer', fontFamily: 'inherit' }}>Annuler</button>
        <button onClick={onConfirm} style={{ flex: 1, padding: '0.65rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'linear-gradient(135deg, #b91c1c, #ef4444)', color: '#fff', fontWeight: 700, fontSize: '0.83rem', cursor: 'pointer', fontFamily: 'inherit' }}>Supprimer</button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   PAGE PRINCIPALE
───────────────────────────────────────────────────────────────── */
const ShopStore = ({ shop }) => {
  const navigate     = useNavigate();
  const { pack, maxProducts, canAddProduct } = useShopPack(shop);

  const [tab,        setTab       ] = useState('products'); // 'products' | 'categories'
  const [products,   setProducts  ] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading   ] = useState(true);
  const [modal,      setModal     ] = useState(null);  // { mode, item? }
  const [deleteTarget, setDeleteTarget] = useState(null); // { type, item }

  /* ── Chargement temps réel ── */
  useEffect(() => {
    if (!shop?.id) return;
    const qProd = query(collection(db, 'shops', shop.id, 'products'),  orderBy('createdAt', 'desc'));
    const qCat  = query(collection(db, 'shops', shop.id, 'categories'), orderBy('createdAt', 'asc'));
    const u1 = onSnapshot(qProd, snap => { setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    const u2 = onSnapshot(qCat,  snap => { setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() }))); });
    return () => { u1(); u2(); };
  }, [shop?.id]);

  /* ── Suppression ── */
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { type, item } = deleteTarget;
    const colRef = collection(db, 'shops', shop.id, type === 'product' ? 'products' : 'categories');
    await deleteDoc(doc(colRef, item.id));
    if (type === 'product' && item.images?.length) {
      for (const url of item.images) await deleteImage(url);
    }
    setDeleteTarget(null);
  }, [deleteTarget, shop?.id]);

  /* ── Toggle statut rapide ── */
  const toggleStatus = useCallback(async (type, item) => {
    const newStatus = item.status === 'published' ? 'draft' : 'published';
    const colRef = collection(db, 'shops', shop.id, type === 'product' ? 'products' : 'categories');
    await updateDoc(doc(colRef, item.id), { status: newStatus, updatedAt: serverTimestamp() });
  }, [shop?.id]);

  if (!shop || loading) return <Loading message="Chargement de la boutique…"/>;

  const list          = tab === 'products' ? products : categories;
  const publishedProd = products.filter(p => p.status === 'published').length;
  const totalProd     = products.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(145deg, #0b142d, #1b2a6b)',
        padding: '0.85rem 1rem',
        color: '#fff',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        <button onClick={() => navigate('/merchant')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', flexShrink: 0 }}>
          <ArrowLeft size={18}/>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Ma boutique</div>
          <div style={{ fontSize: '0.70rem', opacity: 0.72, marginTop: '0.1rem' }}>
            {publishedProd} produit{publishedProd > 1 ? 's' : ''} publié{publishedProd > 1 ? 's' : ''} · Pack <strong>{pack.name}</strong>
          </div>
        </div>
        {/* Badge pack */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '999px', padding: '0.25rem 0.65rem',
          fontSize: '0.72rem', fontWeight: 700, color: '#fff',
          border: '1px solid rgba(255,255,255,0.25)',
        }}>
          <Crown size={11}/>
          {pack.name}
        </div>
      </div>

      {/* ── Barre d'onglets ── */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--outline)',
        background: 'var(--bg)', position: 'sticky', top: 0, zIndex: 10,
      }}>
        {[
          { key: 'products',   label: 'Produits',    icon: Package, count: totalProd },
          { key: 'categories', label: 'Catégories',  icon: Tag,     count: categories.length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '0.75rem 0.5rem',
              background: 'none', border: 'none',
              borderBottom: `2.5px solid ${tab === t.key ? 'var(--primary)' : 'transparent'}`,
              color: tab === t.key ? 'var(--primary)' : 'var(--text-2)',
              fontWeight: 700, fontSize: '0.82rem',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.35rem', transition: 'all 0.15s',
            }}
          >
            <t.icon size={14} strokeWidth={2}/>
            {t.label}
            <span style={{
              background: tab === t.key ? 'var(--primary)' : 'var(--bg-elevated)',
              color: tab === t.key ? '#fff' : 'var(--text-3)',
              borderRadius: '999px', padding: '0.05rem 0.45rem',
              fontSize: '0.65rem', fontWeight: 700,
            }}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* ── Contenu ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>

        {/* Limite pack */}
        {tab === 'products' && maxProducts !== Infinity && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.55rem 0.75rem',
            borderRadius: 'var(--r-md)',
            background: totalProd >= maxProducts ? 'rgba(239,68,68,0.08)' : 'rgba(0,201,167,0.06)',
            border: `1px solid ${totalProd >= maxProducts ? 'rgba(239,68,68,0.20)' : 'rgba(0,201,167,0.15)'}`,
            fontSize: '0.74rem', fontWeight: 600,
            color: totalProd >= maxProducts ? '#ef4444' : 'var(--text-2)',
            marginBottom: '0.65rem',
          }}>
            <Package size={12} style={{ flexShrink: 0 }}/>
            {totalProd}/{maxProducts} produits · Pack {pack.name}
            {totalProd >= maxProducts && (
              <button
                onClick={() => navigate('/merchant/store/pack')}
                style={{
                  marginLeft: 'auto', padding: '0.2rem 0.6rem',
                  borderRadius: '999px', border: 'none',
                  background: '#ef4444', color: '#fff',
                  fontSize: '0.65rem', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                }}
              >
                <Crown size={10}/> Upgrader
              </button>
            )}
          </div>
        )}

        {/* Liste vide */}
        {list.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>
              {tab === 'products' ? '📦' : '🏷️'}
            </div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text)', marginBottom: '0.4rem' }}>
              {tab === 'products' ? 'Aucun produit' : 'Aucune catégorie'}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              {tab === 'products'
                ? 'Ajoutez votre premier produit pour le montrer à vos clients.'
                : 'Les catégories permettent d\'organiser vos produits.'}
            </div>
            <button
              onClick={() => setModal({ mode: tab === 'products' ? 'product' : 'category' })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.65rem 1.25rem',
                borderRadius: 'var(--r-full)', border: 'none',
                background: 'linear-gradient(135deg, #1b2a6b, #00c9a7)',
                color: '#fff', fontWeight: 700, fontSize: '0.85rem',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <Plus size={15}/>
              {tab === 'products' ? 'Ajouter un produit' : 'Ajouter une catégorie'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {list.map(item => {
              const stCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
              const cat   = categories.find(c => c.id === item.categoryId);
              return (
                <div key={item.id} style={{
                  background: 'var(--bg)',
                  borderRadius: 'var(--r-lg)',
                  boxShadow: 'var(--neu-sm)',
                  overflow: 'hidden',
                  display: 'flex', alignItems: 'stretch',
                }}>
                  {/* Bande couleur statut */}
                  <div style={{ width: 3, background: stCfg.color, flexShrink: 0 }}/>

                  {/* Image produit */}
                  {tab === 'products' && (
                    <div style={{
                      width: 52, height: 52, flexShrink: 0,
                      background: 'var(--bg-elevated)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      alignSelf: 'center', margin: '0 0.5rem 0 0.5rem',
                    }}>
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 'var(--r-sm)' }}/>
                      ) : (
                        <Package size={20} color="var(--text-3)" strokeWidth={1.5}/>
                      )}
                    </div>
                  )}

                  {/* Contenu */}
                  <div style={{ flex: 1, padding: '0.65rem 0.5rem', minWidth: 0 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                      marginBottom: cat || item.price != null ? '0.2rem' : 0,
                    }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                      </span>
                      <span style={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, color: stCfg.color, background: stCfg.bg, borderRadius: '999px', padding: '0.1rem 0.45rem' }}>
                        {stCfg.label}
                      </span>
                    </div>
                    {(cat || (item.price != null && item.showPrice !== false)) && (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.72rem', color: 'var(--text-3)', fontWeight: 500 }}>
                        {cat && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}><Tag size={10}/>{cat.name}</span>}
                        {item.price != null && item.showPrice !== false && <span style={{ color: '#00c9a7', fontWeight: 700 }}>{item.price.toLocaleString()} DA</span>}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.1rem', padding: '0 0.5rem', flexShrink: 0 }}>
                    <button
                      onClick={() => toggleStatus(tab === 'products' ? 'product' : 'category', item)}
                      title={item.status === 'published' ? 'Passer en brouillon' : 'Publier'}
                      style={{ background: 'none', border: 'none', padding: '0.4rem', cursor: 'pointer', color: item.status === 'published' ? '#00c9a7' : 'var(--text-3)', borderRadius: 'var(--r-sm)' }}
                    >
                      {item.status === 'published' ? <Eye size={15}/> : <EyeOff size={15}/>}
                    </button>
                    <button
                      onClick={() => setModal({ mode: `edit-${tab === 'products' ? 'product' : 'category'}`, item })}
                      style={{ background: 'none', border: 'none', padding: '0.4rem', cursor: 'pointer', color: 'var(--text-2)', borderRadius: 'var(--r-sm)' }}
                    >
                      <Pencil size={14}/>
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ type: tab === 'products' ? 'product' : 'category', item })}
                      style={{ background: 'none', border: 'none', padding: '0.4rem', cursor: 'pointer', color: '#ef4444', borderRadius: 'var(--r-sm)' }}
                    >
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bouton flottant ajouter ── */}
      {(tab === 'products' ? canAddProduct(totalProd) : true) && (
        <button
          onClick={() => setModal({ mode: tab === 'products' ? 'product' : 'category' })}
          style={{
            position: 'fixed', bottom: 'calc(var(--nav-h, 60px) + 1rem)', right: '1rem',
            width: 50, height: 50, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1b2a6b, #00c9a7)',
            border: 'none', boxShadow: '0 4px 16px rgba(0,201,167,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', zIndex: 100,
            color: '#fff',
          }}
        >
          <Plus size={22} strokeWidth={2.5}/>
        </button>
      )}

      {/* ── Modals ── */}
      {modal && (
        <CrudModal
          mode={modal.mode}
          item={modal.item}
          categories={categories}
          shopId={shop.id}
          pack={pack}
          onClose={() => setModal(null)}
          onSaved={() => {}}
        />
      )}
      {deleteTarget && (
        <DeleteConfirm
          label={deleteTarget.item.name}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default ShopStore;